import React from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import {
  Transfer,
  TransferMechanism,
  TransferFee,
  TransferDeliveryMethod,
  WireAccountType,
  WireType,
  AchRelationshipState,
  CashieringView,
  CashieringRoute,
  CashieringManageAction,
  TransferRecipientBank,
  TransferBankIdentifierType,
  AchRelationship,
  ApprovalMethod,
  TransferInstruction,
  TransferRecipientBankState,
  TransferIRAContributionType,
  TransferIRADistributionReason,
  ScheduledTransfer,
} from '@tradingblock/types';
import { LoadingImage, Text } from '@tradingblock/components';
import { maskAccountNumber } from './data';
import { CashieringViewFormValues } from '../pages/views/cashiering/data/useCashieringView';
import { TradingBlockLink } from '../components/basic/TradingBlockLink';

export const getCurrentTransferView = () => {
  if (window.location.pathname == '/accounts/oauth') {
    return CashieringView.OAuthPlaidRedirect;
  }

  const view = window.location.hash.replace(/^#/, '');
  if (_.includes(_.values(CashieringView), view)) {
    return view as CashieringView;
  }
  return undefined;
};

export const getNextTransferView = (view: CashieringView | undefined, route: CashieringRoute | undefined) => {
  switch (view) {
    case CashieringView.Authorize:
      return route === CashieringRoute.Accounts ? CashieringView.Manage : CashieringView.Amount;
    case CashieringView.Amount:
      return CashieringView.Type;
    case CashieringView.Type:
      return CashieringView.Review;
    case CashieringView.OAuthPlaidRedirect:
      return CashieringView.Manage;
    case CashieringView.ScheduledTransferType:
      return CashieringView.ScheduledTransferAmount;
    case CashieringView.ScheduledTransferAmount:
      return CashieringView.ScheduledTransferReview;
    default:
      return undefined;
  }
};

interface PrimaryButtonOptions {
  view: CashieringView | undefined;
  route: CashieringRoute | undefined;
  action?: CashieringManageAction;
  values?: CashieringViewFormValues;
}

export const getViewPrimaryButton = (
  options: PrimaryButtonOptions,
  isDisabled: boolean,
  isSaving: boolean,
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
) => {
  const { action } = options;
  const contents = getViewPrimaryButtonContents(options);
  if (contents) {
    return (
      <button
        className="btn btn-primary"
        style={action === CashieringManageAction.Delete ? getTransferActionStyle(action, 'backgroundColor') : undefined}
        type="button"
        disabled={isDisabled || isSaving}
        onClick={onClick}
      >
        {contents} {isSaving && <LoadingImage />}
      </button>
    );
  }
  return null;
};

const getViewPrimaryButtonContents = (options: PrimaryButtonOptions) => {
  const { view, route, action, values } = options;
  const mechanism = values && values.transfer && values.transfer.mechanism;
  const objectType = mechanism === 'Ach' ? 'account' : 'bank';
  const defaultButton = (
    <>
      Continue <i className="fal fa-arrow-right" />
    </>
  );
  if (action === CashieringManageAction.AddOptions) {
    return defaultButton;
  }
  if (action === CashieringManageAction.Add) {
    const approvalMethod = values && values.account && values.account.approvalMethod;
    const plaidPublicToken = values && values.account && values.account.plaidPublicToken;
    if (approvalMethod === 'PDF') {
      return null;
    }
    if (approvalMethod === 'Plaid' && !plaidPublicToken) {
      return null;
    }
    return <>{`${mechanism === 'Ach' ? 'Link' : 'Save'} ${objectType}`}</>;
  }
  if (action === CashieringManageAction.Rename) {
    return <>{`Save ${objectType}`}</>;
  }
  if (action === CashieringManageAction.Delete) {
    return <>{`Delete ${objectType}`}</>;
  }
  if (action === CashieringManageAction.Verify) {
    return <>Verify account</>;
  }
  if (action === CashieringManageAction.Close) {
    return <>Close</>;
  }

  switch (view) {
    case CashieringView.Authorize:
      if (values && values.authorizeFailed) {
        return null;
      }
      return defaultButton;
    case CashieringView.Manage:
      return <>Close</>;
    case CashieringView.Review:
      return <>Submit {route === CashieringRoute.Withdraw ? 'withdrawal' : 'deposit'}</>;
    default:
      return defaultButton;
  }
};

export const showPrimaryButton = (options: PrimaryButtonOptions) => {
  const { view, route, action, values } = options;
  const mechanism = values && values.transfer && values.transfer.mechanism;

  if (
    !action &&
    view === CashieringView.Type &&
    route === CashieringRoute.Deposit &&
    (mechanism === 'Wire' || mechanism === 'Check')
  ) {
    // At this time, you can only deposit funds via a linked bank account
    return false;
  }

  const primaryButton = getViewPrimaryButtonContents(options);
  // show primary button only if some contents present
  return !_.isNull(primaryButton);
};

export const getTransferRouteTitle = (route: CashieringRoute | undefined) => {
  switch (route) {
    case CashieringRoute.Withdraw:
      return (
        <>
          <i className="fas fa-inbox-out" /> Withdraw funds
        </>
      );
    case CashieringRoute.Deposit:
      return (
        <>
          <i className="fas fa-inbox-in" /> Fund account
        </>
      );
    case CashieringRoute.Accounts:
      return (
        <>
          <i className="fas fa-university" /> Manage accounts
        </>
      );
    default:
      return null;
  }
};

export const getTransferViewSubtitle = (view: CashieringView | undefined) => {
  switch (view) {
    case CashieringView.Authorize:
      return 'Verify identity';
    case CashieringView.Review:
      return 'Review';
    default:
      return '';
  }
};

export const getTransferActionStyle = (
  action: CashieringManageAction | undefined,
  key: 'color' | 'backgroundColor' = 'color'
) => {
  if (action === CashieringManageAction.Rename) {
    return { [key]: 'var(--green-lighten)' };
  }
  if (action === CashieringManageAction.Delete) {
    return { [key]: 'var(--red)' };
  }
  return undefined;
};

export const getTransferActionIconClass = (action: CashieringManageAction | undefined) => {
  if (action === CashieringManageAction.Rename) {
    return 'fa-pencil';
  }
  if (action === CashieringManageAction.Delete) {
    return 'fa-trash';
  }
  return undefined;
};

export const getTransferTypeLabel = (transferMechanism: TransferMechanism | undefined) => {
  switch (transferMechanism) {
    case 'Ach':
      return 'Linked account';
    case 'Check':
      return 'Paper Check';
    case 'Wire':
      return 'Wire';
  }
};

export const getTransferFeeText = (transferFee: TransferFee | undefined) => {
  if (transferFee && transferFee.customerDebitAmount) {
    return `$${transferFee.customerDebitAmount} Customer Debit`;
  }
  if (transferFee && transferFee.firmCreditAmount) {
    return `$${transferFee.firmCreditAmount} Firm Credit`;
  }
  if (transferFee && transferFee.correspondentNetAmount) {
    return `$${transferFee.correspondentNetAmount} Correspondent Net`;
  }
  return '';
};

const getTransferStatusName = (transfer: Transfer | undefined) => {
  if (transfer && transfer.state === 'Complete') {
    return 'Completed';
  }
  if (transfer && transfer.state === 'Canceled') {
    return 'Cancelled';
  }
  if (transfer && !_.startsWith(transfer.state, 'Pending')) {
    return _.startCase(transfer.state);
  }
  // default to pending status
  return 'Pending';
};

export const getTransferBeneficiaryNickName = (recipientBank: TransferRecipientBank | undefined) => {
  // beneficiaries are tied to recipient banks in the background by nickname
  if (recipientBank) {
    return `TransferBeneficiary-for-RecipientBank.${recipientBank.id}`;
  }
  return undefined;
};

export const getTransferInstructionNickName = (transferInstruction: TransferInstruction | undefined) => {
  // instructions are tied to recipient banks in the background by nickname
  if (transferInstruction) {
    return `TransferInstruction-for-RecipientBank.${transferInstruction.id}`;
  }
  return undefined;
};

export const getApprovalMethod = (approvalMethod: ApprovalMethod | number | undefined): ApprovalMethod | 'Legacy' => {
  // handle raw enum number values
  switch (approvalMethod) {
    case 1:
      return 'MicroDeposit';
    case 2:
      return 'Plaid';
    default:
      return _.isString(approvalMethod) ? approvalMethod : 'Legacy';
  }
};

export const getApprovalMethodLabel = (approvalMethod: ApprovalMethod | undefined) => {
  switch (approvalMethod) {
    case 'Plaid':
      return (
        <>
          <strong>Use your bank login</strong> (recommended)
        </>
      );
    case 'MicroDeposit':
      return <strong>Verify through small deposits</strong>;
    case 'PDF':
      return <strong>Print and send in a paper application</strong>;
  }
};

export const getApprovalMethodDescription = (approvalMethod: ApprovalMethod | undefined, route: CashieringRoute) => {
  switch (approvalMethod) {
    case 'Plaid':
      return 'Works for most US banks - usually takes a few minutes (bank account and routing numbers may be requested)';
    case 'MicroDeposit':
      return 'Works for any bank in the US - usually takes 1-2 business days';
    case 'PDF':
      return (
        <>
          If both of the above fail, download the{' '}
          <TradingBlockLink to="AchRelationshipPdfApplicationUrl">
            <strong>PDF application</strong>
          </TradingBlockLink>
          {route != CashieringRoute.Accounts && (
            <> (or select one of the tabs above to transfer funds via wire or check)</>
          )}
        </>
      );
  }
};

export const isNotYetCreatedStatus = (status: AchRelationshipState) => status === 'PendingCreateApproval';
export const isPendingStatus = (status: AchRelationshipState) =>
  status === 'Pending' || status === 'PendingFirmApproval';
export const isApprovedStatus = (status: AchRelationshipState) => status === 'Approved';
export const isRejectedStatus = (status: AchRelationshipState | TransferRecipientBankState) =>
  status === 'CreateRejected' || status === 'Rejected';
export const isCancelledStatus = (status: AchRelationshipState) => status === 'Cancelled';
export const isNotYetCreatedOrPendingStatus = (status: AchRelationshipState | TransferRecipientBankState) =>
  isNotYetCreatedStatus(status) || isPendingStatus(status);
export const isRejectedOrCancelledStatus = (status: AchRelationshipState | TransferRecipientBankState) =>
  isRejectedStatus(status) || isCancelledStatus(status);
export const isRejectedCancelledOrPendingStatus = (status: AchRelationshipState | TransferRecipientBankState) => {
  return (
    isRejectedStatus(status) || isCancelledStatus(status) || isPendingStatus(status) || isNotYetCreatedStatus(status)
  );
};

export const getTransferStatusText = (transfer: Transfer | undefined) => {
  let label = getTransferStatusName(transfer);
  // if the transfer is for a ira contribution or distribution, we need to add the type to the status
  if (transfer) {
    if (transfer.iraContribution) {
      label += `: ${getFormattedTransferIRAContributionType(transfer.iraContribution.type)}`;
    }
    if (transfer.iraDistribution) {
      label += `: ${getFormattedTransferIRADistributionReason(transfer.iraDistribution.reason)}`;
    }
  }
  if (label === 'Pending') {
    label +=
      transfer && transfer.estimatedFundsAvailableDate
        ? ': Expected ' + dayjs(transfer.estimatedFundsAvailableDate).format('MMM D')
        : '';
  }
  if (label === 'Cancelled') {
    label = `*${label}*`;
  }

  // if the label is rejected, then we want to append the rejectReason onto the label following the formatted reason, then add the formatted type/reason on a new line
  if (transfer && transfer.rejectReason) {
    // add the rejectReason on a new line
    label += ` - (${formatRejectReason(transfer.rejectReason)})`;
  }
  return label;
};

export const getTransferWireType = (type: WireType | undefined) => {
  if (type === WireType.International) {
    return WireType.International;
  }
  return WireType.Domestic;
};

export const getTransferWireTypeFromRecipientBank = (instruction: TransferInstruction | undefined) => {
  if (
    instruction &&
    instruction.recipientBank &&
    instruction.recipientBank.identifierType === TransferBankIdentifierType.BIC
  ) {
    return WireType.International;
  }
  return WireType.Domestic;
};

export const getTransferBankIdentifierType = (data: Partial<TransferRecipientBank>) => {
  if (data.identifierType === TransferBankIdentifierType.BIC) {
    return TransferBankIdentifierType.BIC;
  }
  return TransferBankIdentifierType.ABA;
};

export const getTransferMethod = (
  data: Transfer | ScheduledTransfer,
  relatedData: { achRelationships?: AchRelationship[]; wireInstructions?: TransferInstruction[] }
) => {
  const label = getTransferTypeLabel(data.mechanism);
  switch (data.mechanism) {
    case 'Ach':
      const achRelationshipId = _.get(data, 'achRelationship'); // should really be named 'achRelationshipId' coming from TB api...
      const achRelationship =
        relatedData.achRelationships && _.find(relatedData.achRelationships, r => r.id === achRelationshipId);
      return (
        <>
          {label}
          <br />
          {achRelationship && achRelationship.bankAccount}
        </>
      );
    case 'Check':
      return (
        <>
          {label}
          <br />
          {_.join(_.filter(data.memos, m => !!m), ', ')}
        </>
      );
    case 'Wire':
      const type =
        relatedData.wireInstructions &&
        getTransferWireTypeFromRecipientBank(_.find(relatedData.wireInstructions, b => b.id === data.instructionId));
      return (
        <>
          {label}
          <br />
          {type && _.startCase(getTransferWireType(type))}
        </>
      );
  }
};

export const getDeliveryMethodLabel = (deliveryMethod: TransferDeliveryMethod | undefined) => {
  switch (deliveryMethod) {
    case 'Overnight':
      return 'Overnight Express Mail';
    case 'Standard':
      return 'US Regular Mail';
  }
};

export const getWireAccountTypeLabel = (accountType: WireAccountType | undefined) => {
  switch (accountType) {
    case WireAccountType.SelfOwned:
      return 'Myself';
    case WireAccountType.ThirdParty:
      return 'Third party';
  }
};

export const getFormattedAchRelationship = (acct: Partial<AchRelationship>) => {
  return (
    <div className="mb-3">
      <label>Nickname:</label> <span className="mute">{acct.nickName}</span>
      {acct.bankName ? (
        <div className="mt-3">
          <label>Bank account:</label>{' '}
          <span className="mute">
            {acct.bankName} ({acct.bankAccount})
          </span>
        </div>
      ) : (
        acct.bankAccount
      )}
    </div>
  );
};

export const getFormattedRecipientBank = (acct: Partial<TransferRecipientBank>) => {
  return (
    <div className="mb-1">
      <label>Nickname:</label> <span className="mute">{acct.nickName}</span>
      <label>ABA/Routing:</label> <span className="mute">{acct.identifier}</span>
      {acct.name && (
        <>
          <label>Bank:</label> {acct.name}
        </>
      )}
      {acct.city && (
        <div className="mb-3 mt-3">
          {acct.city}
          {acct.city && acct.stateProvince ? ', ' : ''}
          {acct.stateProvince} {acct.postalCode}
          <Text id={`defaults.country.${acct.country}`} type="field" />
        </div>
      )}
      {acct.intermediaryId && <div className="mb-3">via Intermediary</div>}
    </div>
  );
};

export const getFormattedTransferInstruction = (acct: Partial<TransferInstruction>) => {
  return (
    <div className="transfer-instruction">
      <div className="instruction-field">
        <div>Nickname:</div>
        <div className="mute">{acct.nickName}</div>
      </div>
      <div className="instruction-field">
        <div>ABA/Routing:</div>
        <div className="mute">{acct.recipientBank && acct.recipientBank.identifier}</div>
      </div>
      {acct.name && (
        <div className="instruction-field">
          <div>Bank:</div>
          <div className="mute">{acct.name}</div>
        </div>
      )}
      {acct.city && (
        <div className="instruction-field">
          <div>Location:</div>
          <div className="mute">
            {acct.city}
            {acct.city && acct.stateProvince ? ', ' : ''}
            {acct.stateProvince} {acct.postalCode}
            <div>
              <Text id={`defaults.country.${acct.country}`} type="field" />
            </div>
          </div>
        </div>
      )}
      {acct.intermediary && (
        <div className="instruction-field">
          <div>Routing:</div>
          <div className="mute">via Intermediary</div>
        </div>
      )}
    </div>
  );
};

export const isDataEmpty = (data: object, omit: string[] = []): boolean => {
  const values: any[] = _.values(_.omit(data, omit));
  return _.every(values, v => (_.isObject(v) ? isDataEmpty(v) : _.isEmpty(v)));
};

export const getFormattedTransferIRAContributionType = (type?: TransferIRAContributionType) => {
  if (!type) {
    return 'No type specified';
  }
  switch (type) {
    case 'ReCharacterization':
      return 'Re-characterization';
    case 'ApexRollover60Day':
      return '60-Day Rollover';
    case 'ApexRolloverDirect':
      return 'Direct Rollover';
    case 'ApexTrusteeFee':
      return 'Trustee Fee';
    case 'RqdRollover':
      return 'Rollover';
    case 'RqdSep':
      return 'Simplified Employee Pension (SEP)';
    case 'RqdCatchup':
      return 'Catch-up';
    default:
      return type;
  }
};

export const getFormattedTransferIRADistributionReason = (reason: TransferIRADistributionReason | undefined) => {
  if (!reason) {
    return reason;
  }
  switch (reason) {
    case 'ExcessContributionRemovalBeforeTaxDeadline':
      return 'Excess Contribution Removal Before Tax Deadline';
    case 'ExcessContributionRemovalAfterTaxDeadline':
      return 'Excess Contribution Removal After Tax Deadline';
    case 'RolloverToQualifiedPlan':
      return 'Rollover To Qualified Plan';
    case 'ReCharacterizationPriorYear':
      return 'Re-characterization Prior Year';
    case 'ReCharacterizationCurrentYear':
      return 'Re-characterization Current Year';
    case 'ApexTransfer':
      return 'Transfer';
    case 'ApexManagementFee':
      return 'Management Fee';
    case 'ApexNormalRothIRAGreaterThan5Years':
      return 'Normal Roth IRA Greater Than 5 Years';
    case 'ApexPrematureSimpleIRALessThan2Years':
      return 'Premature Simple IRA Less Than 2 Years';
    case 'RqdPrematureExceptionApplies':
      return 'Premature Exception Applies';
    case 'RqdDeathRemoveExcessCurrentYear':
      return 'Death Remove Excess Current Year';
    case 'RqdDeathRemoveExcessPriorYear':
      return 'Death Remove Excess Prior Year';
    case 'RqdDeathRemoveExcessTwoYearsPrior':
      return 'Death Remove Excess Two Years Prior';
    case 'RqdDeathRolloverForSurvivingSpouse':
      return 'Death Rollover For Surviving Spouse';
    case 'RqdRemoveExcessExceptionApplies':
      return 'Remove Excess Exception Applies';
    case 'RqdRemoveExcessTwoYearsPrior':
      return 'Remove Excess Two Years Prior';
    case 'RqdRemoveExcessTwoYearsPriorExceptionApplies':
      return 'Remove Excess Two Years Prior Exception Applies';
    case 'RqdRemoveExcessPriorYearExceptionApplies':
      return 'Remove Excess Prior Year Exception Applies';
    case 'RqdExcessAnnualAdditions':
      return 'Excess Annual Additions';
    case 'RqdQualifiedDistribution':
      return 'Qualified Distribution';
    case 'RqdDistributionExceptionApplies':
      return 'Distribution Exception Applies';
    default:
      return reason;
  }
};

export const getDistributionDescription = (reason?: TransferIRADistributionReason) => {
  if (!reason) {
    return 'Unknown Distribution Reason';
  }
  const descriptions: { [key: string]: string } = {
    Disability: 'Distribution due to the IRA holder being disabled, following IRS definitions.',
    Premature: 'Distribution taken before age 59½, possibly subject to early withdrawal penalties.',
    Death: "Distribution to beneficiaries upon the IRA owner's death.",
    ExcessContributionRemovalBeforeTaxDeadline:
      'Removal of contributions over the limit before the tax deadline to avoid penalties.',
    ExcessContributionRemovalAfterTaxDeadline:
      'Removal of excess contributions after the tax deadline, possibly incurring penalties.',
    RolloverToQualifiedPlan: 'Transfer of funds from an IRA to another qualified retirement plan.',
    ReCharacterizationPriorYear: 'Reversal of a contribution to a different IRA type for the prior year.',
    ReCharacterizationCurrentYear: 'Reversal of a contribution to a different IRA type for the current year.',
    RqdPrematureExceptionApplies:
      'Required distribution for which an exception to the early withdrawal penalty applies.',
    RqdDeathRemoveExcessCurrentYear: 'Removal of excess contributions due to death in the current year.',
    RqdDeathRemoveExcessPriorYear: 'Removal of excess contributions due to death in the prior year.',
    RqdDeathRemoveExcessTwoYearsPrior: 'Removal of excess contributions due to death two years prior.',
    RqdDeathRolloverForSurvivingSpouse: "Rollover of funds to the surviving spouse upon the IRA owner's death.",
    RqdRemoveExcessExceptionApplies: 'Removal of excess contributions where an exception applies.',
    RqdRemoveExcessTwoYearsPrior: 'Removal of excess contributions from two years prior.',
    RqdRemoveExcessTwoYearsPriorExceptionApplies:
      'Removal of excess contributions from two years prior where an exception applies.',
    RqdRemoveExcessPriorYearExceptionApplies:
      'Removal of excess contributions from the prior year where an exception applies.',
    Normal: 'Regular distribution usually taken after age 59½ without penalties.',
    RqdExcessAnnualAdditions: 'Removal of excess annual additions beyond the contribution limits.',
    RqdQualifiedDistribution:
      'A distribution that meets criteria to be considered qualified and potentially tax and penalty-free.',
    RqdDistributionExceptionApplies: 'A distribution for which an exception to penalties applies.',
    ApexTransfer: 'A transfer of funds within Apex clearing systems or similar entities.',
    ApexManagementFee: 'Fees taken from the IRA for management services.',
    ApexPrematureSimpleIRALessThan2Years:
      'Early distribution from a SIMPLE IRA that is less than two years old, subject to penalties.',
    ApexNormalRothIRAGreaterThan5Years:
      'A qualified distribution from a Roth IRA that has been held for more than five years.',
    // You can continue to add more key-value pairs for additional reasons.
  };
  return descriptions[reason] || 'Unknown Distribution Reason';
};

export const getContributionDescription = (type?: TransferIRAContributionType) => {
  if (!type) {
    return 'Unknown Contribution Type';
  }

  const descriptions: { [key: string]: string } = {
    Regular: 'A contribution made within the annual limit set by the IRS for traditional or Roth IRAs.',
    ReCharacterization:
      'The process of changing the designation of a previously made IRA contribution from one type of IRA to another (e.g., from a Roth IRA to a Traditional IRA or vice versa).',
    ApexRollover60Day:
      'A rollover where funds are distributed to the individual and redeposited into another retirement account within 60 days.',
    ApexRolloverDirect:
      'A direct transfer of retirement funds from one account to another without the funds being paid to the individual, commonly used to move assets between qualified plans or IRAs.',
    ApexTrusteeFee:
      'Fees charged by the trustee for the administration and management of an IRA account, including account maintenance and other related services.',
    Employer: "Contributions made by an employer to an employee's retirement plan, such as a SEP IRA or a SIMPLE IRA.",
    Employee:
      'Contributions made by an employee to their own retirement plan, typically through salary deferrals into plans like a 401(k) or 403(b).',
    Transfer:
      'The process of moving IRA assets directly from one trustee or custodian to another without the assets being paid to the account holder, typically not a taxable event.',
    RqdRollover:
      'A rollover involving required minimum distributions (RMDs), which are mandatory withdrawals from retirement accounts starting at a certain age.',
    RqdCatchup:
      "Contributions beyond the standard limit allowed for individuals aged 50 or older, providing an opportunity to 'catch up' on retirement savings.",
    RqdSep:
      'Contributions to a Simplified Employee Pension (SEP) plan, typically made by employers, allowing for higher contribution limits than standard IRAs.',
    Conversion:
      'The process of converting assets from a Traditional IRA to a Roth IRA, which involves paying taxes on the pre-tax assets converted.',
  };

  return descriptions[type] || 'Unknown Contribution Type';
};

export const formatRejectReason = (reason: string) => {
  // Split the string into words by camel case or hyphens
  const words = reason.split(/([A-Z][a-z]+|-)/);

  // Capitalize the first letter of each word, except for IRA
  const formattedWords = words.map(word => {
    if (word === 'IRA') return word;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  // Join the formatted words back into a string
  return formattedWords.join(' ');
};

export const formatDateOfDeath = (date: { year: number; month: number; day: number } | string): string => {
  // Check if the date is an object with year, month, day properties
  if (typeof date === 'object' && 'year' in date && 'month' in date && 'day' in date) {
    const { year, month, day } = date;
    // JavaScript's Date expects month in the range 0-11, so we need to subtract 1 from the provided month.
    const dateObject = new Date(year, month - 1, day);
    return dateObject.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } else if (typeof date === 'string') {
    // If the date is already a string, attempt to parse it as a Date object
    const dateObject = new Date(date);
    if (!isNaN(dateObject.getTime())) {
      return dateObject.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } else {
      // Handle invalid date string
      return 'Invalid date';
    }
  } else {
    // Handle the case where the date doesn't match either expected format
    return 'Invalid date';
  }
};
